import config from "../../config";
import { getlist, post } from "../../contexts/apicontexts";
import {
  getTaskCurrentOwner,
  getTaskCurrentTimelineStatus,
  getTaskHistoryActionLabel,
  isTaskCompleted,
  normalizeTaskHistoryActionType,
  normalizeTaskPriority,
  normalizeTaskProgressPercent,
  normalizeTaskStatus,
  resolveTaskProgressState,
  sortTaskHistoryByRecent,
  sortTasksByRecent,
} from "./taskUtils";

const TASK_STORAGE_KEY = "crm.support.tasks.v1";
let remoteTaskMode = "unknown";

const safeParse = (value, fallback) => {
  if (!value) return fallback;

  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const toNumberId = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const extractApiErrorMessage = (response, fallbackMessage) => {
  if (!response) return fallbackMessage;
  if (typeof response === "string") return response;
  if (typeof response?.message === "string" && response.message.trim()) return response.message;
  if (typeof response?.error === "string" && response.error.trim()) return response.error;
  if (typeof response?.title === "string" && response.title.trim()) return response.title;

  const validationMessages = Object.values(response?.errors || {})
    .flat()
    .filter(Boolean)
    .map((item) => String(item).trim())
    .filter(Boolean);

  if (validationMessages.length) return validationMessages[0];
  return fallbackMessage;
};

const isRemoteUnavailableResponse = (response) => !response || response?.error === "Network Error";

const buildTaskListUrl = ({ scopeUserId = "", ticketId = 0 } = {}) => {
  const params = new URLSearchParams();

  if (scopeUserId) params.set("userId", scopeUserId);
  if (Number(ticketId || 0) > 0) params.set("ticketId", String(Number(ticketId)));

  const query = params.toString();
  return `${config.baseurl}Task/getlist${query ? `?${query}` : ""}`;
};

const buildAllTaskListUrl = ({ ticketId = 0 } = {}) => {
  const params = new URLSearchParams();
  if (Number(ticketId || 0) > 0) params.set("ticketId", String(Number(ticketId)));

  const query = params.toString();
  return `${config.baseurl}Task/all${query ? `?${query}` : ""}`;
};

const buildMyTaskListUrl = ({ userId = "", ticketId = 0 } = {}) => {
  const params = new URLSearchParams();
  if (userId) params.set("userId", userId);
  if (Number(ticketId || 0) > 0) params.set("ticketId", String(Number(ticketId)));

  const query = params.toString();
  return `${config.baseurl}Task/my${query ? `?${query}` : ""}`;
};

const buildTicketCountsUrl = ({ scopeUserId = "" } = {}) => {
  const params = new URLSearchParams();
  if (scopeUserId) params.set("userId", scopeUserId);

  const query = params.toString();
  return `${config.baseurl}Task/ticketcounts${query ? `?${query}` : ""}`;
};

const buildMyTaskHistoryUrl = ({ userId = "" } = {}) => {
  const params = new URLSearchParams();
  if (userId) params.set("userId", userId);

  const query = params.toString();
  return `${config.baseurl}Task/myhistory${query ? `?${query}` : ""}`;
};

const buildMyTaskHistoryPagedPayload = ({
  userId = "",
  pageNumber = 1,
  pageSize = 10,
  searchText = "",
  status = "",
  assignedEmployeeId = 0,
  startDate = "",
  endDate = "",
  priority = "",
  actionType = "",
} = {}) => ({
  userId: userId || "",
  pageNumber: Number(pageNumber || 1),
  pageSize: Number(pageSize || 10),
  searchText: searchText || "",
  status: status && status !== "All" ? status : "",
  assignedEmployeeId: Number(assignedEmployeeId || 0) || null,
  startDate: startDate || "",
  endDate: endDate || "",
  priority: priority && priority !== "All" ? priority : "",
  actionType: actionType && actionType !== "All" ? actionType : "",
  sortDirection: "DESC",
});

const buildMyTaskObserversPagedPayload = ({
  userId = "",
  pageNumber = 1,
  pageSize = 10,
  searchText = "",
  status = "",
  assignedEmployeeId = 0,
  startDate = "",
  endDate = "",
  priority = "",
  source = "",
} = {}) => ({
  userId: userId || "",
  pageNumber: Number(pageNumber || 1),
  pageSize: Number(pageSize || 10),
  searchText: searchText || "",
  status: status && status !== "All" ? status : "",
  assignedEmployeeId: Number(assignedEmployeeId || 0) || null,
  startDate: startDate || "",
  endDate: endDate || "",
  priority: priority && priority !== "All" ? priority : "",
  source: source && source !== "All" ? source : "",
  sortDirection: "DESC",
});

const buildTaskListPagedPayload = ({
  userId = "",
  ticketId = 0,
  pageNumber = 1,
  pageSize = 10,
  searchText = "",
  status = "",
  assignedEmployeeId = 0,
} = {}) => ({
  userId: userId || "",
  ticketId: Number(ticketId || 0) || null,
  pageNumber: Number(pageNumber || 1),
  pageSize: Number(pageSize || 10),
  searchText: searchText || "",
  status: status && status !== "All" ? status : "",
  assignedEmployeeId: Number(assignedEmployeeId || 0) || null,
  sortDirection: "DESC",
});

export const normalizeEmployee = (employee) => {
  const id = employee?.id ?? employee?.Id ?? employee?.ID ?? employee?.empId ?? 0;
  const userId = employee?.userID ?? employee?.userId ?? employee?.UserID ?? employee?.UserId ?? employee?.empUserId ?? "";
  const fullName = employee?.fullName ?? employee?.FullName ?? employee?.name ?? employee?.Name ?? "";
  const employeeCode = employee?.employeeCode ?? employee?.EmployeeCode ?? "";
  const employeeNumber = employee?.employeeNumber ?? employee?.EmployeeNumber ?? "";
  const workEmail = employee?.workEmail ?? employee?.WorkEmail ?? employee?.email ?? employee?.Email ?? "";

  return {
    id: toNumberId(id),
    userId: String(userId || "").trim(),
    fullName: String(fullName || "").trim(),
    employeeCode: String(employeeCode || "").trim(),
    employeeNumber: String(employeeNumber || "").trim(),
    workEmail: String(workEmail || "").trim(),
  };
};

export const buildEmployeeLabel = (employee) => {
  const normalized = normalizeEmployee(employee);
  const suffix = normalized.employeeCode || normalized.employeeNumber;
  return `${normalized.fullName || "Employee"}${suffix ? ` (${suffix})` : ""}`;
};

const normalizeHistoryItem = (item) => {
  const actionType = normalizeTaskHistoryActionType(item?.actionType ?? item?.ActionType ?? item?.action ?? item?.Action);
  const actionLabel = getTaskHistoryActionLabel(actionType, item?.actionLabel ?? item?.ActionLabel ?? item?.action ?? item?.Action);

  return {
    id: item?.id ?? item?.Id ?? `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    actionType,
    actionLabel,
    note: String(item?.note ?? item?.Note ?? ""),
    actorUserId: String(item?.actorUserId ?? item?.ActorUserId ?? ""),
    actorName: String(item?.actorName ?? item?.ActorName ?? ""),
    status: item?.status ?? item?.Status ? normalizeTaskStatus(item?.status ?? item?.Status) : "",
    progressPercent: normalizeTaskProgressPercent(item?.progressPercent ?? item?.ProgressPercent),
    fromAssignedEmployeeId: toNumberId(item?.fromAssignedEmployeeId ?? item?.FromAssignedEmployeeId),
    fromAssignedUserId: String(item?.fromAssignedUserId ?? item?.FromAssignedUserId ?? ""),
    fromAssignedEmployeeName: String(item?.fromAssignedEmployeeName ?? item?.FromAssignedEmployeeName ?? ""),
    toAssignedEmployeeId: toNumberId(item?.toAssignedEmployeeId ?? item?.ToAssignedEmployeeId),
    toAssignedUserId: String(item?.toAssignedUserId ?? item?.ToAssignedUserId ?? ""),
    toAssignedEmployeeName: String(item?.toAssignedEmployeeName ?? item?.ToAssignedEmployeeName ?? ""),
    createdAt: item?.createdAt ?? item?.CreatedAt ?? new Date().toISOString(),
  };
};

const buildFallbackTaskHistory = (task) => {
  const fallbackItems = [];

  if (task?.createdAt ?? task?.CreatedAt) {
    fallbackItems.push(
      normalizeHistoryItem({
        actionType: "Created",
        actionLabel: "Task Created",
        note: `Assigned to ${task?.assignedEmployeeName ?? task?.AssignedEmployeeName ?? "team member"}`,
        actorUserId: task?.createdByUserId ?? task?.CreatedByUserId ?? "",
        actorName: task?.createdByName ?? task?.CreatedByName ?? "User",
        status: task?.status ?? task?.Status ?? "Pending",
        progressPercent: task?.progressPercent ?? task?.ProgressPercent ?? 0,
        toAssignedEmployeeId: task?.assignedEmployeeId ?? task?.AssignedEmployeeId ?? 0,
        toAssignedUserId: task?.assignedUserId ?? task?.AssignedUserId ?? "",
        toAssignedEmployeeName: task?.assignedEmployeeName ?? task?.AssignedEmployeeName ?? "",
        createdAt: task?.createdAt ?? task?.CreatedAt,
      })
    );
  }

  if (task?.lastForwardedAt ?? task?.LastForwardedAt) {
    fallbackItems.push(
      normalizeHistoryItem({
        actionType: "Forwarded",
        actionLabel: "Task Forwarded",
        note: "Forward history before timeline support is partially available.",
        actorName: task?.lastForwardedBy ?? task?.LastForwardedBy ?? "User",
        status: task?.status ?? task?.Status ?? "Pending",
        progressPercent: task?.progressPercent ?? task?.ProgressPercent ?? 0,
        toAssignedEmployeeId: task?.assignedEmployeeId ?? task?.AssignedEmployeeId ?? 0,
        toAssignedUserId: task?.assignedUserId ?? task?.AssignedUserId ?? "",
        toAssignedEmployeeName: task?.assignedEmployeeName ?? task?.AssignedEmployeeName ?? "",
        createdAt: task?.lastForwardedAt ?? task?.LastForwardedAt,
      })
    );
  }

  return sortTaskHistoryByRecent(fallbackItems);
};

export const normalizeTask = (task) => {
  const assignedEmployee = normalizeEmployee(task);
  const historyItems = Array.isArray(task?.history ?? task?.History)
    ? (task?.history ?? task?.History).map(normalizeHistoryItem)
    : buildFallbackTaskHistory(task);
  const progressState = resolveTaskProgressState(
    task?.status ?? task?.Status,
    task?.progressPercent ?? task?.ProgressPercent
  );

  return {
    id: task?.id ?? task?.Id ?? task?.taskId ?? task?.TaskId ?? `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    ticketId: toNumberId(task?.ticketId ?? task?.TicketId),
    ticketNumber: String(task?.ticketNumber ?? task?.TicketNumber ?? ""),
    ticketSubject: String(task?.ticketSubject ?? task?.TicketSubject ?? task?.subject ?? task?.Subject ?? ""),
    ticketStatus: String(task?.ticketStatus ?? task?.TicketStatus ?? ""),
    title: String(task?.title ?? task?.Title ?? ""),
    description: String(task?.description ?? task?.Description ?? ""),
    status: progressState.status,
    priority: normalizeTaskPriority(task?.priority ?? task?.Priority),
    progressPercent: progressState.progressPercent,
    dueDate: task?.dueDate ?? task?.DueDate ?? "",
    assignedEmployeeId: toNumberId(
      task?.assignedEmployeeId ?? task?.AssignedEmployeeId ?? task?.empId ?? task?.EmpId ?? assignedEmployee.id
    ),
    assignedUserId: String((task?.assignedUserId ?? task?.AssignedUserId ?? task?.empUserId ?? task?.EmpUserId ?? assignedEmployee.userId) || ""),
    assignedEmployeeName: String(
      task?.assignedEmployeeName ??
        task?.AssignedEmployeeName ??
        task?.assignedToName ??
        task?.AssignedToName ??
        assignedEmployee.fullName ??
        ""
    ),
    assignedEmployeeEmail: String(
      (task?.assignedEmployeeEmail ?? task?.AssignedEmployeeEmail ?? task?.assignedToEmail ?? task?.AssignedToEmail ?? assignedEmployee.workEmail) || ""
    ),
    createdByUserId: String(task?.createdByUserId ?? task?.CreatedByUserId ?? task?.createdUserId ?? task?.CreatedUserId ?? ""),
    createdByName: String(task?.createdByName ?? task?.CreatedByName ?? task?.createdUserName ?? task?.CreatedUserName ?? ""),
    sourceType: String(task?.sourceType ?? task?.SourceType ?? "SupportTicket"),
    createdAt: task?.createdAt ?? task?.CreatedAt ?? new Date().toISOString(),
    updatedAt: task?.updatedAt ?? task?.UpdatedAt ?? task?.createdAt ?? task?.CreatedAt ?? new Date().toISOString(),
    lastForwardedAt: task?.lastForwardedAt ?? task?.LastForwardedAt ?? "",
    lastForwardedBy: String(task?.lastForwardedBy ?? task?.LastForwardedBy ?? ""),
    history: sortTaskHistoryByRecent(historyItems),
    files: normalizeTaskFiles(task?.files ?? task?.Files),
    observers: normalizeTaskObservers(task?.observers ?? task?.Observers),
    replies: normalizeTaskReplies(task?.replies ?? task?.Replies),
  };
};

const normalizeTaskFiles = (rawFiles) =>
  Array.isArray(rawFiles)
    ? rawFiles.map((f) => ({
        id: f?.id ?? f?.Id ?? 0,
        replyId: f?.replyId ?? f?.ReplyId ?? null,
        fileName: f?.fileName ?? f?.FileName ?? "",
        fileUrl: f?.fileUrl ?? f?.FileUrl ?? "",
        fileSize: f?.fileSize ?? f?.FileSize ?? 0,
        contentType: f?.contentType ?? f?.ContentType ?? null,
        createdAt: f?.createdAt ?? f?.CreatedAt ?? null,
      }))
    : [];

export const normalizeTaskObserver = (observer) => ({
  id: observer?.id ?? observer?.Id ?? 0,
  employeeId: toNumberId(observer?.employeeId ?? observer?.EmployeeId),
  userId: String(observer?.userId ?? observer?.UserId ?? ""),
  employeeName: String(observer?.employeeName ?? observer?.EmployeeName ?? ""),
  employeeEmail: String(observer?.employeeEmail ?? observer?.EmployeeEmail ?? ""),
  addedByName: String(observer?.addedByName ?? observer?.AddedByName ?? ""),
  source: String(observer?.source ?? observer?.Source ?? "Manual"),
  createdAt: observer?.createdAt ?? observer?.CreatedAt ?? null,
});

const normalizeTaskObservers = (rawObservers) =>
  Array.isArray(rawObservers) ? rawObservers.map(normalizeTaskObserver) : [];

export const normalizeTaskReply = (reply) => ({
  id: reply?.id ?? reply?.Id ?? 0,
  taskId: toNumberId(reply?.taskId ?? reply?.TaskId),
  type: String(reply?.type ?? reply?.Type ?? "Reply"),
  message: String(reply?.message ?? reply?.Message ?? ""),
  authorUserId: String(reply?.authorUserId ?? reply?.AuthorUserId ?? ""),
  authorName: String(reply?.authorName ?? reply?.AuthorName ?? ""),
  createdAt: reply?.createdAt ?? reply?.CreatedAt ?? null,
  files: normalizeTaskFiles(reply?.files ?? reply?.Files),
});

const normalizeTaskReplies = (rawReplies) =>
  Array.isArray(rawReplies) ? rawReplies.map(normalizeTaskReply) : [];

export const normalizeMyTaskHistoryItem = (item) => ({
  historyId: toNumberId(item?.historyId ?? item?.HistoryId ?? item?.id ?? item?.Id),
  taskId: toNumberId(item?.supportTaskId ?? item?.SupportTaskId ?? item?.taskId ?? item?.TaskId),
  ticketId: toNumberId(item?.ticketId ?? item?.TicketId),
  ticketNumber: String(item?.ticketNumber ?? item?.TicketNumber ?? ""),
  ticketSubject: String(item?.ticketSubject ?? item?.TicketSubject ?? ""),
  ticketStatus: String(item?.ticketStatus ?? item?.TicketStatus ?? ""),
  taskTitle: String(item?.taskTitle ?? item?.TaskTitle ?? ""),
  taskDescription: String(item?.taskDescription ?? item?.TaskDescription ?? ""),
  taskPriority: normalizeTaskPriority(item?.taskPriority ?? item?.TaskPriority),
  taskStatus: normalizeTaskStatus(item?.taskStatus ?? item?.TaskStatus),
  taskProgressPercent: normalizeTaskProgressPercent(item?.taskProgressPercent ?? item?.TaskProgressPercent),
  historyProgressPercent: normalizeTaskProgressPercent(item?.historyProgressPercent ?? item?.HistoryProgressPercent),
  taskDueDate: item?.taskDueDate ?? item?.TaskDueDate ?? "",
  assignedEmployeeId: toNumberId(item?.assignedEmployeeId ?? item?.AssignedEmployeeId),
  assignedUserId: String(item?.assignedUserId ?? item?.AssignedUserId ?? ""),
  assignedEmployeeName: String(item?.assignedEmployeeName ?? item?.AssignedEmployeeName ?? ""),
  assignedEmployeeEmail: String(item?.assignedEmployeeEmail ?? item?.AssignedEmployeeEmail ?? ""),
  createdByUserId: String(item?.createdByUserId ?? item?.CreatedByUserId ?? ""),
  createdByName: String(item?.createdByName ?? item?.CreatedByName ?? ""),
  sourceType: String(item?.sourceType ?? item?.SourceType ?? ""),
  actionType: normalizeTaskHistoryActionType(item?.actionType ?? item?.ActionType),
  actionLabel: getTaskHistoryActionLabel(item?.actionType ?? item?.ActionType, item?.actionLabel ?? item?.ActionLabel),
  note: String(item?.note ?? item?.Note ?? ""),
  actorUserId: String(item?.actorUserId ?? item?.ActorUserId ?? ""),
  actorName: String(item?.actorName ?? item?.ActorName ?? ""),
  historyStatus: normalizeTaskStatus(item?.historyStatus ?? item?.HistoryStatus ?? item?.status ?? item?.Status),
  progressPercent: normalizeTaskProgressPercent(item?.progressPercent ?? item?.ProgressPercent ?? item?.historyProgressPercent ?? item?.HistoryProgressPercent),
  fromAssignedEmployeeId: toNumberId(item?.fromAssignedEmployeeId ?? item?.FromAssignedEmployeeId),
  fromAssignedUserId: String(item?.fromAssignedUserId ?? item?.FromAssignedUserId ?? ""),
  fromAssignedEmployeeName: String(item?.fromAssignedEmployeeName ?? item?.FromAssignedEmployeeName ?? ""),
  toAssignedEmployeeId: toNumberId(item?.toAssignedEmployeeId ?? item?.ToAssignedEmployeeId),
  toAssignedUserId: String(item?.toAssignedUserId ?? item?.ToAssignedUserId ?? ""),
  toAssignedEmployeeName: String(item?.toAssignedEmployeeName ?? item?.ToAssignedEmployeeName ?? ""),
  createdAt: item?.createdAt ?? item?.CreatedAt ?? "",
});

export const normalizeMyTaskObserverItem = (item) => ({
  observerId: toNumberId(item?.observerId ?? item?.ObserverId ?? item?.id ?? item?.Id),
  taskId: toNumberId(item?.supportTaskId ?? item?.SupportTaskId ?? item?.taskId ?? item?.TaskId),
  ticketId: toNumberId(item?.ticketId ?? item?.TicketId),
  ticketNumber: String(item?.ticketNumber ?? item?.TicketNumber ?? ""),
  ticketSubject: String(item?.ticketSubject ?? item?.TicketSubject ?? ""),
  ticketStatus: String(item?.ticketStatus ?? item?.TicketStatus ?? ""),
  taskTitle: String(item?.taskTitle ?? item?.TaskTitle ?? ""),
  taskDescription: String(item?.taskDescription ?? item?.TaskDescription ?? ""),
  taskPriority: normalizeTaskPriority(item?.taskPriority ?? item?.TaskPriority),
  taskStatus: normalizeTaskStatus(item?.taskStatus ?? item?.TaskStatus),
  taskProgressPercent: normalizeTaskProgressPercent(item?.taskProgressPercent ?? item?.TaskProgressPercent),
  taskDueDate: item?.taskDueDate ?? item?.TaskDueDate ?? "",
  assignedEmployeeId: toNumberId(item?.assignedEmployeeId ?? item?.AssignedEmployeeId),
  assignedUserId: String(item?.assignedUserId ?? item?.AssignedUserId ?? ""),
  assignedEmployeeName: String(item?.assignedEmployeeName ?? item?.AssignedEmployeeName ?? ""),
  assignedEmployeeEmail: String(item?.assignedEmployeeEmail ?? item?.AssignedEmployeeEmail ?? ""),
  createdByUserId: String(item?.createdByUserId ?? item?.CreatedByUserId ?? ""),
  createdByName: String(item?.createdByName ?? item?.CreatedByName ?? ""),
  sourceType: String(item?.sourceType ?? item?.SourceType ?? ""),
  observerEmployeeId: toNumberId(item?.observerEmployeeId ?? item?.ObserverEmployeeId),
  observerUserId: String(item?.observerUserId ?? item?.ObserverUserId ?? ""),
  observerEmployeeName: String(item?.observerEmployeeName ?? item?.ObserverEmployeeName ?? ""),
  observerEmployeeEmail: String(item?.observerEmployeeEmail ?? item?.ObserverEmployeeEmail ?? ""),
  addedByName: String(item?.addedByName ?? item?.AddedByName ?? ""),
  source: String(item?.source ?? item?.Source ?? "Manual"),
  createdAt: item?.createdAt ?? item?.CreatedAt ?? "",
});

const normalizePaginatedMyTaskObserverResult = (result) => ({
  items: Array.isArray(result?.items ?? result?.Items)
    ? (result?.items ?? result?.Items).map(normalizeMyTaskObserverItem)
    : [],
  totalCount: toNumberId(result?.totalCount ?? result?.TotalCount),
  totalPages: toNumberId(result?.totalPages ?? result?.TotalPages),
  pageNumber: toNumberId(result?.pageNumber ?? result?.PageNumber) || 1,
  pageSize: toNumberId(result?.pageSize ?? result?.PageSize) || 10,
  hasNextPage: Boolean(result?.hasNextPage ?? result?.HasNextPage),
  hasPreviousPage: Boolean(result?.hasPreviousPage ?? result?.HasPreviousPage),
});

const normalizePaginatedMyTaskHistoryResult = (result) => ({
  items: Array.isArray(result?.items ?? result?.Items)
    ? (result?.items ?? result?.Items).map(normalizeMyTaskHistoryItem)
    : [],
  totalCount: toNumberId(result?.totalCount ?? result?.TotalCount),
  totalPages: toNumberId(result?.totalPages ?? result?.TotalPages),
  pageNumber: toNumberId(result?.pageNumber ?? result?.PageNumber) || 1,
  pageSize: toNumberId(result?.pageSize ?? result?.PageSize) || 10,
  hasNextPage: Boolean(result?.hasNextPage ?? result?.HasNextPage),
  hasPreviousPage: Boolean(result?.hasPreviousPage ?? result?.HasPreviousPage),
});

const normalizePaginatedTaskListResult = (result) => ({
  items: Array.isArray(result?.items ?? result?.Items)
    ? (result?.items ?? result?.Items).map(normalizeTask)
    : [],
  totalCount: toNumberId(result?.totalCount ?? result?.TotalCount),
  totalPages: toNumberId(result?.totalPages ?? result?.TotalPages),
  pageNumber: toNumberId(result?.pageNumber ?? result?.PageNumber) || 1,
  pageSize: toNumberId(result?.pageSize ?? result?.PageSize) || 10,
  hasNextPage: Boolean(result?.hasNextPage ?? result?.HasNextPage),
  hasPreviousPage: Boolean(result?.hasPreviousPage ?? result?.HasPreviousPage),
  summary: {
    total: toNumberId(result?.summary?.total ?? result?.Summary?.Total),
    pending: toNumberId(result?.summary?.pending ?? result?.Summary?.Pending),
    inProgress: toNumberId(result?.summary?.inProgress ?? result?.Summary?.InProgress),
    waiting: toNumberId(result?.summary?.waiting ?? result?.Summary?.Waiting),
    completed: toNumberId(result?.summary?.completed ?? result?.Summary?.Completed),
  },
});

const readLocalTasks = () => {
  if (typeof window === "undefined") return [];
  const raw = window.localStorage.getItem(TASK_STORAGE_KEY);
  return sortTasksByRecent(safeParse(raw, []).map(normalizeTask));
};

const writeLocalTasks = (items) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(TASK_STORAGE_KEY, JSON.stringify(sortTasksByRecent(items)));
};

const filterTasks = (items, { scopeUserId = "", ticketId = 0 } = {}) =>
  sortTasksByRecent(items).filter((task) => {
    const currentOwner = getTaskCurrentOwner(task);
    const matchesScope = scopeUserId ? String(currentOwner.assignedUserId || "") === String(scopeUserId) : true;
    const matchesTicket = ticketId ? Number(task.ticketId || 0) === Number(ticketId) : true;
    return matchesScope && matchesTicket;
  });

const buildTicketTaskCountMap = (items, options = {}) =>
  filterTasks(items, options).reduce((result, task) => {
    const ticketId = Number(task.ticketId || 0);
    if (!ticketId) return result;
    result[ticketId] = (result[ticketId] || 0) + 1;
    return result;
  }, {});

const remotePayloadFromTask = (task, historyNote = "") => {
  const progressState = resolveTaskProgressState(task.status, task.progressPercent);

  return {
    Id: task.id,
    TicketId: Number(task.ticketId || 0),
    TicketNumber: task.ticketNumber || "",
    TicketSubject: task.ticketSubject || "",
    TicketStatus: task.ticketStatus || "",
    Title: task.title || "",
    Description: task.description || "",
    Status: progressState.status,
    Priority: normalizeTaskPriority(task.priority),
    ProgressPercent: progressState.progressPercent,
    DueDate: task.dueDate || null,
    AssignedEmployeeId: Number(task.assignedEmployeeId || 0),
    AssignedUserId: task.assignedUserId || "",
    AssignedEmployeeName: task.assignedEmployeeName || "",
    AssignedEmployeeEmail: task.assignedEmployeeEmail || "",
    CreatedByUserId: task.createdByUserId || "",
    CreatedByName: task.createdByName || "",
    SourceType: task.sourceType || "SupportTicket",
    LastForwardedAt: task.lastForwardedAt || null,
    LastForwardedBy: task.lastForwardedBy || "",
    HistoryNote: historyNote || null,
    ObserverEmployeeIds: Array.isArray(task.observerEmployeeIds)
      ? task.observerEmployeeIds.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
      : undefined,
  };
};

const buildRemoteFormData = (payload, files) => {
  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    if (Array.isArray(value)) {
      if (value.length === 0) {
        // An empty string value would make ASP.NET's model binder try to parse "" as a long and fail
        // validation. Send a sentinel (-1) instead — the server filters out non-positive ids, so this
        // still resolves to an explicit empty list rather than being treated as "field not provided".
        formData.append(key, "-1");
        return;
      }
      value.forEach((item) => formData.append(key, String(item)));
      return;
    }
    formData.append(key, value instanceof Date ? value.toISOString() : String(value));
  });
  (files || []).forEach((file) => {
    if (file instanceof File) formData.append("Files", file);
  });
  return formData;
};

const buildHistoryEntry = (actionType, actionLabel, note, identity, metadata = {}) => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  actionType: normalizeTaskHistoryActionType(actionType || actionLabel),
  actionLabel: getTaskHistoryActionLabel(actionType, actionLabel),
  note: String(note || ""),
  actorUserId: String(identity?.userId || ""),
  actorName: String(identity?.fullName || identity?.email || "User"),
  status: metadata.status ? normalizeTaskStatus(metadata.status) : "",
  progressPercent: normalizeTaskProgressPercent(metadata.progressPercent),
  fromAssignedEmployeeId: toNumberId(metadata.fromAssignedEmployeeId),
  fromAssignedUserId: String(metadata.fromAssignedUserId || ""),
  fromAssignedEmployeeName: String(metadata.fromAssignedEmployeeName || ""),
  toAssignedEmployeeId: toNumberId(metadata.toAssignedEmployeeId),
  toAssignedUserId: String(metadata.toAssignedUserId || ""),
  toAssignedEmployeeName: String(metadata.toAssignedEmployeeName || ""),
  createdAt: new Date().toISOString(),
});

const createLocalTask = (task, identity) => {
  const items = readLocalTasks();
  const progressState = resolveTaskProgressState(task.status, task.progressPercent);
  const nextTask = normalizeTask({
    ...task,
    status: progressState.status,
    progressPercent: progressState.progressPercent,
    id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    createdByUserId: identity?.userId || task.createdByUserId,
    createdByName: identity?.fullName || identity?.email || task.createdByName,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    history: [
      buildHistoryEntry("Created", "Task Created", `Assigned to ${task.assignedEmployeeName || "team member"}`, identity, {
        status: progressState.status,
        progressPercent: progressState.progressPercent,
        toAssignedEmployeeId: task.assignedEmployeeId,
        toAssignedUserId: task.assignedUserId,
        toAssignedEmployeeName: task.assignedEmployeeName,
      }),
    ],
  });

  writeLocalTasks([nextTask, ...items]);
  return nextTask;
};

const updateLocalTask = (taskId, updates, identity, historyAction, historyNote, historyMeta = {}) => {
  const items = readLocalTasks();
  let updatedTask = null;

  const nextItems = items.map((item) => {
    if (String(item.id) !== String(taskId)) return item;

    const existingHistory = sortTaskHistoryByRecent(item.history || []);
    const nextHistory = [...existingHistory];
    const progressState = resolveTaskProgressState(
      updates?.status ?? item?.status,
      updates?.progressPercent ?? item?.progressPercent
    );
    const nextTaskStatus = progressState.status;
    const nextTaskProgressPercent = progressState.progressPercent;
    const isForwardAction = normalizeTaskHistoryActionType(historyAction) === "Forwarded";
    const isStatusAction = normalizeTaskHistoryActionType(historyAction) === "StatusChanged";
    const isProgressAction = normalizeTaskHistoryActionType(historyAction) === "ProgressChanged";
    const currentOwnerEmployeeId = historyMeta.fromAssignedEmployeeId ?? item.assignedEmployeeId;
    const currentOwnerUserId = historyMeta.fromAssignedUserId ?? item.assignedUserId;
    const currentOwnerName = historyMeta.fromAssignedEmployeeName ?? item.assignedEmployeeName;

    if (isForwardAction) {
      nextHistory.unshift(
        buildHistoryEntry("Forwarded", "Task Forwarded", historyNote || `Forwarded to ${historyMeta.toAssignedEmployeeName || "another employee"}`, identity, {
          status: "Forwarded",
          progressPercent: item.progressPercent,
          fromAssignedEmployeeId: currentOwnerEmployeeId,
          fromAssignedUserId: currentOwnerUserId,
          fromAssignedEmployeeName: currentOwnerName,
          toAssignedEmployeeId: updates?.assignedEmployeeId,
          toAssignedUserId: updates?.assignedUserId,
          toAssignedEmployeeName: updates?.assignedEmployeeName,
        })
      );

      nextHistory.unshift(
        buildHistoryEntry("Assigned", "Task Assigned", historyNote || `Received from ${currentOwnerName || "previous owner"}.`, identity, {
          status: "Pending",
          progressPercent: 0,
          fromAssignedEmployeeId: currentOwnerEmployeeId,
          fromAssignedUserId: currentOwnerUserId,
          fromAssignedEmployeeName: currentOwnerName,
          toAssignedEmployeeId: updates?.assignedEmployeeId,
          toAssignedUserId: updates?.assignedUserId,
          toAssignedEmployeeName: updates?.assignedEmployeeName,
        })
      );
    } else if (isStatusAction) {
      nextHistory.unshift(
        buildHistoryEntry("StatusChanged", nextTaskStatus === "Completed" ? "Task Completed" : "Status Updated", historyNote || `Status changed to ${nextTaskStatus}`, identity, {
          status: nextTaskStatus,
          progressPercent: nextTaskProgressPercent,
          fromAssignedEmployeeId: currentOwnerEmployeeId,
          fromAssignedUserId: currentOwnerUserId,
          fromAssignedEmployeeName: currentOwnerName,
          toAssignedEmployeeId: updates?.assignedEmployeeId ?? item.assignedEmployeeId,
          toAssignedUserId: updates?.assignedUserId ?? item.assignedUserId,
          toAssignedEmployeeName: updates?.assignedEmployeeName ?? item.assignedEmployeeName,
        })
      );
    } else if (isProgressAction) {
      nextHistory.unshift(
        buildHistoryEntry("ProgressChanged", "Progress Updated", historyNote || `Progress changed to ${nextTaskProgressPercent}%`, identity, {
          status: nextTaskStatus,
          progressPercent: nextTaskProgressPercent,
          fromAssignedEmployeeId: currentOwnerEmployeeId,
          fromAssignedUserId: currentOwnerUserId,
          fromAssignedEmployeeName: currentOwnerName,
          toAssignedEmployeeId: updates?.assignedEmployeeId ?? item.assignedEmployeeId,
          toAssignedUserId: updates?.assignedUserId ?? item.assignedUserId,
          toAssignedEmployeeName: updates?.assignedEmployeeName ?? item.assignedEmployeeName,
        })
      );
    } else {
      nextHistory.unshift(
        buildHistoryEntry("Updated", "Task Updated", historyNote || "Task details were updated.", identity, {
          status: nextTaskStatus,
          progressPercent: nextTaskProgressPercent,
          fromAssignedEmployeeId: currentOwnerEmployeeId,
          fromAssignedUserId: currentOwnerUserId,
          fromAssignedEmployeeName: currentOwnerName,
          toAssignedEmployeeId: updates?.assignedEmployeeId ?? item.assignedEmployeeId,
          toAssignedUserId: updates?.assignedUserId ?? item.assignedUserId,
          toAssignedEmployeeName: updates?.assignedEmployeeName ?? item.assignedEmployeeName,
        })
      );
    }

    updatedTask = normalizeTask({
      ...item,
      ...updates,
      status: isForwardAction ? "Pending" : nextTaskStatus,
      progressPercent: isForwardAction ? 0 : nextTaskProgressPercent,
      updatedAt: new Date().toISOString(),
      history: nextHistory,
    });

    return updatedTask;
  });

  if (!updatedTask) {
    throw new Error("Task not found.");
  }

  writeLocalTasks(nextItems);
  return updatedTask;
};

const shouldUseRemote = () => Boolean(config.baseurl);

export const fetchEmployees = async () => {
  try {
    const response = await getlist(`${config.baseurl}Employee/getlist`);
    if (response?.status === 200 && Array.isArray(response.data)) {
      return response.data.map(normalizeEmployee).filter((item) => item.id || item.userId || item.fullName);
    }
  } catch {
    return [];
  }

  return [];
};

export const getTaskList = async (options = {}) => {
  if (shouldUseRemote()) {
    try {
      const response = await getlist(buildTaskListUrl(options));
      if (response?.status === 200 && Array.isArray(response.data)) {
        remoteTaskMode = "available";
        return filterTasks(response.data.map(normalizeTask), options);
      }
      remoteTaskMode = "unavailable";
    } catch {
      remoteTaskMode = "unavailable";
    }
  }

  return filterTasks(readLocalTasks(), options);
};

export const getAllTaskList = async (options = {}) => {
  if (shouldUseRemote()) {
    try {
      const response = await getlist(buildAllTaskListUrl(options));
      if (response?.status === 200 && Array.isArray(response.data)) {
        remoteTaskMode = "available";
        return sortTasksByRecent(response.data.map(normalizeTask));
      }
      remoteTaskMode = "unavailable";
    } catch {
      remoteTaskMode = "unavailable";
    }
  }

  return filterTasks(readLocalTasks(), { ticketId: options.ticketId });
};

export const getMyTaskList = async (options = {}) => {
  const normalizedOptions = {
    scopeUserId: options.userId || options.scopeUserId || "",
    ticketId: options.ticketId || 0,
  };

  if (shouldUseRemote()) {
    try {
      const response = await getlist(buildMyTaskListUrl({
        userId: normalizedOptions.scopeUserId,
        ticketId: normalizedOptions.ticketId,
      }));
      if (response?.status === 200 && Array.isArray(response.data)) {
        remoteTaskMode = "available";
        return filterTasks(response.data.map(normalizeTask), normalizedOptions);
      }
      remoteTaskMode = "unavailable";
    } catch {
      remoteTaskMode = "unavailable";
    }
  }

  return filterTasks(readLocalTasks(), normalizedOptions);
};

export const getTicketTaskCounts = async (options = {}) => {
  if (shouldUseRemote()) {
    try {
      const response = await getlist(buildTicketCountsUrl(options));
      if (response?.status === 200 && Array.isArray(response.data)) {
        remoteTaskMode = "available";
        return response.data.reduce((result, item) => {
          const ticketId = toNumberId(item?.ticketId ?? item?.TicketId);
          if (!ticketId) return result;
          result[ticketId] = toNumberId(item?.taskCount ?? item?.TaskCount);
          return result;
        }, {});
      }
    } catch {
      // Fall back below, but do not permanently disable remote retries.
    }
  }

  return buildTicketTaskCountMap(readLocalTasks(), options);
};

export const getMyTaskHistory = async (options = {}) => {
  if (!shouldUseRemote()) {
    throw new Error("Task history API is not available.");
  }

  try {
    const response = await getlist(buildMyTaskHistoryUrl(options));
    if (response?.status === 200 && Array.isArray(response.data)) {
      remoteTaskMode = "available";
      return response.data.map(normalizeMyTaskHistoryItem);
    }
  } catch {
    remoteTaskMode = "unavailable";
    throw new Error("Task history API is not available.");
  }

  throw new Error("Task history could not be loaded.");
};

export const getMyTaskHistoryPaged = async (options = {}) => {
  const payload = buildMyTaskHistoryPagedPayload(options);

  if (!shouldUseRemote()) {
    throw new Error("Task history API is not available.");
  }

  const response = await post(`${config.baseurl}Task/myhistory/paged`, payload);
  if (response?.status === 200) {
    remoteTaskMode = "available";
    return normalizePaginatedMyTaskHistoryResult(response.data || {});
  }

  if (!isRemoteUnavailableResponse(response)) {
    throw new Error(extractApiErrorMessage(response, "Task history could not be loaded."));
  }

  remoteTaskMode = "unavailable";
  throw new Error("Task history API is not available.");
};

export const getMyTaskObserversPaged = async (options = {}) => {
  const payload = buildMyTaskObserversPagedPayload(options);

  if (!shouldUseRemote()) {
    throw new Error("Task observers API is not available.");
  }

  const response = await post(`${config.baseurl}Task/myobservers/paged`, payload);
  if (response?.status === 200) {
    remoteTaskMode = "available";
    return normalizePaginatedMyTaskObserverResult(response.data || {});
  }

  if (!isRemoteUnavailableResponse(response)) {
    throw new Error(extractApiErrorMessage(response, "Task observers could not be loaded."));
  }

  remoteTaskMode = "unavailable";
  throw new Error("Task observers API is not available.");
};

export const getAllTaskListPaged = async (options = {}) => {
  const payload = buildTaskListPagedPayload(options);
  const response = await post(`${config.baseurl}Task/all/paged`, payload);
  if (response?.status === 200) {
    remoteTaskMode = "available";
    return normalizePaginatedTaskListResult(response.data || {});
  }

  throw new Error(extractApiErrorMessage(response, "All task workspace could not be loaded."));
};

export const getMyTaskListPaged = async (options = {}) => {
  const payload = buildTaskListPagedPayload(options);
  const response = await post(`${config.baseurl}Task/my/paged`, payload);
  if (response?.status === 200) {
    remoteTaskMode = "available";
    return normalizePaginatedTaskListResult(response.data || {});
  }

  throw new Error(extractApiErrorMessage(response, "My task workspace could not be loaded."));
};

export const createTask = async (task, identity) => {
  if (shouldUseRemote()) {
    const payload = remotePayloadFromTask(task);
    // The backend action binds via [FromForm], so the request body must always be FormData, even without files/observers.
    const body = buildRemoteFormData(payload, task.files);
    const response = await post(`${config.baseurl}Task/add`, body);
    if (response?.status === 200) {
      remoteTaskMode = "available";
      return normalizeTask(response.data || task);
    }

    if (!isRemoteUnavailableResponse(response)) {
      throw new Error(extractApiErrorMessage(response, "Task could not be created."));
    }
    remoteTaskMode = "unavailable";
  }

  return createLocalTask(task, identity);
};

export const updateTask = async (taskId, updates, identity, historyAction = "Task Updated", historyNote = "", historyMeta = {}) => {
  const payload = remotePayloadFromTask({ ...updates, id: taskId }, historyNote);

  if (shouldUseRemote()) {
    // The backend action binds via [FromForm], so the request body must always be FormData, even without files/observers.
    const body = buildRemoteFormData(payload, updates.files);
    const response = await post(`${config.baseurl}Task/update`, body);
    if (response?.status === 200) {
      remoteTaskMode = "available";
      return normalizeTask(response.data || { ...updates, id: taskId });
    }

    if (!isRemoteUnavailableResponse(response)) {
      throw new Error(extractApiErrorMessage(response, "Task could not be updated."));
    }
    remoteTaskMode = "unavailable";
  }

  return updateLocalTask(taskId, updates, identity, historyAction, historyNote, historyMeta);
};

export const updateTaskStatus = async (task, status, identity) => {
  if (isTaskCompleted(task)) {
    throw new Error("Completed tasks are locked and cannot change status.");
  }

  const progressState = resolveTaskProgressState(status, status === "Completed" ? 100 : task.progressPercent);

  return updateTask(
    task.id,
    { ...task, status: progressState.status, progressPercent: progressState.progressPercent },
    identity,
    "Status Updated",
    `Status changed to ${progressState.status}`,
    {
      fromAssignedEmployeeId: task.assignedEmployeeId,
      fromAssignedUserId: task.assignedUserId,
      fromAssignedEmployeeName: task.assignedEmployeeName,
      toAssignedEmployeeId: task.assignedEmployeeId,
      toAssignedUserId: task.assignedUserId,
      toAssignedEmployeeName: task.assignedEmployeeName,
    }
  );
};

export const updateTaskProgress = async (task, progressPercent, identity) => {
  if (isTaskCompleted(task)) {
    throw new Error("Completed tasks are locked and cannot change progress.");
  }

  const progressState = resolveTaskProgressState(task.status, progressPercent);

  return updateTask(
    task.id,
    { ...task, status: progressState.status, progressPercent: progressState.progressPercent },
    identity,
    "Progress Updated",
    progressState.status === "Completed"
      ? "Progress reached 100% and the task was completed."
      : `Progress updated to ${progressState.progressPercent}%`,
    {
      fromAssignedEmployeeId: task.assignedEmployeeId,
      fromAssignedUserId: task.assignedUserId,
      fromAssignedEmployeeName: task.assignedEmployeeName,
      toAssignedEmployeeId: task.assignedEmployeeId,
      toAssignedUserId: task.assignedUserId,
      toAssignedEmployeeName: task.assignedEmployeeName,
    }
  );
};

export const forwardTask = async (task, assignee, identity, note = "") => {
  if (isTaskCompleted(task)) {
    throw new Error("Completed tasks are locked and cannot be forwarded.");
  }

  const currentTimelineStatus = getTaskCurrentTimelineStatus(task);
  if (currentTimelineStatus === "Forwarded") {
    throw new Error("Forward is locked because the current timeline item is already forwarded.");
  }

  return updateTask(
    task.id,
    {
      ...task,
      status: "Pending",
      progressPercent: 0,
      assignedEmployeeId: assignee.id,
      assignedUserId: assignee.userId,
      assignedEmployeeName: assignee.fullName,
      assignedEmployeeEmail: assignee.workEmail,
      lastForwardedAt: new Date().toISOString(),
      lastForwardedBy: identity?.fullName || identity?.email || "User",
    },
    identity,
    "Task Forwarded",
    note || `Forwarded to ${assignee.fullName || "another employee"}`,
    {
      fromAssignedEmployeeId: task.assignedEmployeeId,
      fromAssignedUserId: task.assignedUserId,
      fromAssignedEmployeeName: task.assignedEmployeeName,
      toAssignedEmployeeId: assignee.id,
      toAssignedUserId: assignee.userId,
      toAssignedEmployeeName: assignee.fullName,
    }
  );
};

export const getTaskById = async (taskId) => {
  if (!shouldUseRemote()) {
    throw new Error("Task details API is not available.");
  }

  const response = await getlist(`${config.baseurl}Task/${taskId}`);
  if (response?.status === 200 && response.data) {
    remoteTaskMode = "available";
    return normalizeTask(response.data);
  }

  throw new Error(extractApiErrorMessage(response, "Task details could not be loaded."));
};

export const addTaskReply = async (taskId, { type = "Reply", message = "", files = [] } = {}) => {
  if (!shouldUseRemote()) {
    throw new Error("Task reply API is not available.");
  }

  const body = buildRemoteFormData(
    {
      TaskId: Number(taskId),
      Type: type,
      Message: message,
    },
    files
  );

  const response = await post(`${config.baseurl}Task/reply/add`, body);
  if (response?.status === 200) {
    return normalizeTaskReply(response.data || {});
  }

  throw new Error(extractApiErrorMessage(response, "Reply could not be sent."));
};

export const deleteTaskFile = async (fileId) => {
  if (!shouldUseRemote()) {
    throw new Error("File delete API is not available.");
  }

  const response = await post(`${config.baseurl}Task/file/delete/${fileId}`, {});
  if (response?.status === 200) {
    return true;
  }

  throw new Error(extractApiErrorMessage(response, "File could not be deleted."));
};
